/**
 * Mini Cart Drawer
 * Slide-out cart with add-to-cart interception and flying animation
 */

(function () {
  'use strict';

  // ===== SELECTORS =====
  const SELECTORS = {
    drawer: '[data-mini-cart-drawer]',
    close: '[data-mini-cart-close]',
    content: '[data-mini-cart-content]',
    items: '[data-mini-cart-items]',
    empty: '[data-mini-cart-empty]',
    footer: '[data-mini-cart-footer]',
    count: '[data-mini-cart-count]',
    subtotal: '[data-mini-cart-subtotal]',
    item: '[data-mini-cart-item]',
    itemQty: '[data-item-qty]',
    qtyChange: '[data-qty-change]',
    removeItem: '[data-remove-item]',
    flyingItem: '[data-flying-item]',
    flyingImage: '[data-flying-item-image]',
    note: '[data-mini-cart-note]',
    noteStatus: '[data-mini-cart-note-status]',
    navLink: '[data-mini-cart-nav]',
    headerCartBtn: '.site-header-cart--button, [data-mobile-header-cart-btn]',
    headerCartCount: '[data-header-cart-count], [data-mobile-header-cart-count]',
    addToCartForm: 'form[action="/cart/add"]',
    quickBuyBtn: '[data-quick-buy]',
    mmQuickAddAction: '[data-mm-quick-add-action="cart"]',
  };

  // ===== STATE =====
  let isOpen = false;
  let isUpdating = false;
  let scrollYBeforeOpen = 0;
  let toastTimer = null;
  const _cartLimitMessages = new Map();

  // ===== NOTE STATE =====
  let noteTimer = null;
  let noteSavedValue = '';
  let noteStatusTimer = null;
  const CART_VERIFY_DELAY_MS = 180;

  // ===== DOM REFERENCES =====
  let drawer, content, items, empty, footer, countEl, subtotalEl, flyingItem, flyingImage, headerCartBtns;
  let noteInput, noteStatusEl;

  // ===== INITIALIZATION =====
  function init() {
    drawer = document.querySelector(SELECTORS.drawer);
    if (!drawer) return;

    content = drawer.querySelector(SELECTORS.content);
    items = drawer.querySelector(SELECTORS.items);
    empty = drawer.querySelector(SELECTORS.empty);
    footer = drawer.querySelector(SELECTORS.footer);
    countEl = drawer.querySelector(SELECTORS.count);
    subtotalEl = drawer.querySelector(SELECTORS.subtotal);
    flyingItem = drawer.querySelector(SELECTORS.flyingItem) || document.querySelector(SELECTORS.flyingItem);
    flyingImage = drawer.querySelector(SELECTORS.flyingImage) || document.querySelector(SELECTORS.flyingImage);
    noteInput = drawer.querySelector(SELECTORS.note);
    noteStatusEl = drawer.querySelector(SELECTORS.noteStatus);
    noteSavedValue = noteInput ? noteInput.value : '';
    headerCartBtns = Array.from(document.querySelectorAll(SELECTORS.headerCartBtn));

    bindEvents();
    interceptAddToCart();
  }

  // ===== EVENT BINDING =====
  function bindEvents() {
    // Close buttons
    drawer.querySelectorAll(SELECTORS.close).forEach(btn => {
      btn.addEventListener('click', closeDrawer);
    });

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && isOpen) {
        closeDrawer();
      }
    });

    // Quantity buttons
    drawer.addEventListener('click', (e) => {
      const qtyBtn = e.target.closest(SELECTORS.qtyChange);
      if (qtyBtn) {
        handleQuantityChange(qtyBtn);
      }

      const removeBtn = e.target.closest(SELECTORS.removeItem);
      if (removeBtn) {
        handleRemoveItem(removeBtn);
      }
    });

    // Header cart button - open drawer instead of navigating
    headerCartBtns.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        openDrawer();
      });
    });

    // Listen for cart update events
    document.addEventListener('cart:update', (event) => {
      if (!isMiniCartEvent(event)) refreshCart();
    });
    document.addEventListener('cart:updated', (event) => {
      if (!isMiniCartEvent(event)) refreshCart();
    });
    document.addEventListener('product-form:cart:update', () => refreshCart());

    // Special instructions / order note
    if (noteInput) {
      noteInput.addEventListener('input', () => {
        if (noteTimer) clearTimeout(noteTimer);
        noteTimer = setTimeout(() => { void saveCartNote(); }, 600);
      });
      noteInput.addEventListener('blur', () => { void saveCartNote(); });
    }

    // Flush the note to the cart before navigating to checkout / cart page
    drawer.querySelectorAll(SELECTORS.navLink).forEach((link) => {
      link.addEventListener('click', async (e) => {
        if (!noteInput || noteInput.value === noteSavedValue) return;
        e.preventDefault();
        const href = link.getAttribute('href');
        try {
          await saveCartNote();
        } catch (error) {
          console.error('Error saving cart note before navigation:', error);
        } finally {
          if (href) window.location.href = href;
        }
      });
    });
  }

  // ===== SAVE CART NOTE =====
  async function saveCartNote() {
    if (!noteInput) return;
    if (noteTimer) {
      clearTimeout(noteTimer);
      noteTimer = null;
    }

    const value = noteInput.value;
    if (value === noteSavedValue) return;

    try {
      await fetchCartJson('cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ note: value })
      }, 'Unable to save note');

      noteSavedValue = value;
      showNoteStatus('Note saved');
    } catch (error) {
      console.error('Error saving cart note:', error);
      showNoteStatus('Could not save note', true);
      throw error;
    }
  }

  function showNoteStatus(message, isError = false) {
    if (!noteStatusEl) return;
    noteStatusEl.textContent = message;
    noteStatusEl.hidden = false;
    noteStatusEl.classList.toggle('mini-cart-drawer__note-status--error', isError);

    if (noteStatusTimer) clearTimeout(noteStatusTimer);
    if (!isError) {
      noteStatusTimer = setTimeout(() => {
        noteStatusEl.hidden = true;
      }, 2000);
    }
  }

  // ===== INTERCEPT ADD TO CART =====
  function interceptAddToCart() {
    // Intercept form submissions
    document.addEventListener('submit', async (e) => {
      const form = e.target.closest(SELECTORS.addToCartForm);
      if (!form) return;

      // Don't intercept if it's a Buy Now action
      if (form.closest('[data-mm-quick-add-action="buy"]') ||
        form.querySelector('[name="checkout"]') ||
        e.submitter?.name === 'checkout') {
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      await handleAddToCart(form, e.submitter || null);
    }, true);

    // Intercept quick buy button clicks
    document.addEventListener('click', async (e) => {
      // Handle mm-quick-add cart action
      const cartAction = e.target.closest(SELECTORS.mmQuickAddAction);
      if (cartAction) {
        e.preventDefault();
        const quickAdd = cartAction.closest('[data-mm-quick-add]');
        const variantId = quickAdd?.querySelector('[data-mm-quick-add-variant-id][aria-selected="true"]')?.dataset.mmQuickAddVariantId ||
          quickAdd?.dataset.selectedVariantId;
        if (variantId) {
          const productItem = cartAction.closest('.productitem, .productgrid--item');
          const image = productItem?.querySelector('.productitem--image img')?.src;
          await addToCartAjax(variantId, 1, image, e);
        }
        return;
      }

      // Handle direct quick-buy for single variant products
      const quickBuy = e.target.closest(SELECTORS.quickBuyBtn);
      if (quickBuy && !quickBuy.closest('[data-mm-quick-add-has-variants="true"]')) {
        const variantId = quickBuy.dataset.variantId;
        if (variantId) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          const productItem = quickBuy.closest('.productitem, .productgrid--item');
          const image = productItem?.querySelector('.productitem--image img')?.src;
          await addToCartAjax(variantId, 1, image, e);
        }
      }
    });
  }

  // ===== ADD TO CART HANDLER =====
  async function handleAddToCart(form, trigger = null) {
    const formData = new FormData(form);
    const variantId = formData.get('id');
    const quantity = parseInt(formData.get('quantity')) || 1;

    // Get product image for flying animation
    const productItem = form.closest('.productitem, .productgrid--item, .product-single');
    const image = productItem?.querySelector('.productitem--image img, .product-gallery img')?.src;

    await addToCartAjax(variantId, quantity, image, null, form, trigger);
  }

  function getScopedLoadingButtons({ form = null, trigger = null } = {}) {
    if (trigger && isElementVisible(trigger)) {
      const syncedPdpButtons = Array.from(
        document.querySelectorAll('[data-product-atc]:not([data-product-atc-hidden]), [data-mm-size-atc]')
      ).filter(isElementVisible);
      if (syncedPdpButtons.length && syncedPdpButtons.includes(trigger)) {
        return syncedPdpButtons;
      }
      return [trigger];
    }

    if (form) {
      const syncedPdpButtons = Array.from(
        document.querySelectorAll('[data-product-atc]:not([data-product-atc-hidden]), [data-mm-size-atc]')
      ).filter(isElementVisible);
      if (syncedPdpButtons.length) {
        return syncedPdpButtons;
      }

      const formButtons = Array.from(
        form.querySelectorAll(
          '[data-product-atc]:not([data-product-atc-hidden]), [data-product-buy-now]:not([data-product-buy-now-hidden])'
        )
      ).filter(isElementVisible);
      return formButtons.length ? [formButtons[0]] : [];
    }

    const quickAddWrapper = trigger?.closest('[data-mm-quick-add]');
    if (quickAddWrapper && quickAddWrapper.closest('.productitem, .productgrid--item')) {
      return Array.from(quickAddWrapper.querySelectorAll('[data-quick-buy]')).filter(isElementVisible);
    }

    return trigger && isElementVisible(trigger) ? [trigger] : [];
  }

  // ===== AJAX ADD TO CART =====
  const _addToCartLock = (() => {
    const factory = window.MrMapleOptimistic?.createActionLock;
    if (!factory) return null;
    return factory({
      async action(variantId, quantity, imageUrl, clickEvent, form, triggerButton, loadingButtons) {
        await completeVerifiedAddToCart(
          [{ id: variantId, quantity: quantity }],
          { imageUrl, startFrame: clickEvent, openDrawer: true }
        );
      },
      render(processing) {
        isUpdating = processing;
      },
      onError(error) {
        showAddToCartError(error);
      },
    });
  })();

  async function completeVerifiedAddToCart(cartItems, options = {}) {
    const normalizedItems = normalizeCartAddItems(cartItems);
    if (!normalizedItems.length) {
      throw new Error('Missing variant ID');
    }

    const result = await addCartItemsWithVerification(normalizedItems);
    const firstItem = normalizedItems[0];
    const addedItem = result.addedItem || normalizeAddedCartItem(result.payload);
    const newlyAddedItem = {
      key: addedItem?.key || null,
      variantId: addedItem?.variant_id || firstItem.id,
      scrollToTop: true,
    };

    if (options.imageUrl && options.startFrame) {
      await animateFlyToCart(options.imageUrl, options.startFrame);
    } else if (options.imageUrl) {
      bounceCartIcon();
    }

    if (isCartPayload(result.cart)) {
      syncCartSnapshot(result.cart, {
        newlyAddedItem,
        preferInPlace: false,
      });
      emitCartChanged(result.cart, 'mini-cart');
    } else {
      // The add was authoritative (add.js returned 2xx), but the cart read used
      // for rendering failed (e.g. an empty/truncated response). Await a refresh
      // before showing anything so we never open a misleading empty drawer for an
      // item that is actually in the cart. If the refresh also fails, confirm the
      // add with a toast instead of presenting an empty cart.
      updateHeaderCartCount();
      const refreshed = await refreshCart(newlyAddedItem);
      if (!refreshed) {
        showToast('Item added to your cart', 'success');
        return result;
      }
    }

    if (options.openDrawer !== false) {
      openDrawer();
    }

    return result;
  }

  async function addToCartAjax(variantId, quantity, imageUrl, clickEvent, form, triggerButton = null, options = {}) {
    const button = triggerButton ||
      form?.querySelector('[type="submit"]') ||
      clickEvent?.target.closest('button');
    const loadingButtons = Array.from(new Set(getScopedLoadingButtons({ form, trigger: button })));

    if (_addToCartLock) {
      if (_addToCartLock.isLocked()) {
        if (options && options.rethrow === true) {
          throw new Error('Cart is already updating. Please try again.');
        }
        return null;
      }
      loadingButtons.forEach((b) => b.classList.add('processing'));
      try {
        return await _addToCartLock.execute(variantId, quantity, imageUrl, clickEvent, form, triggerButton, loadingButtons);
      } catch (error) {
        // createActionLock has already routed the failure through onError.
        // Customer-facing add failures are handled outcomes unless an internal
        // caller explicitly asks to receive the rejection.
        if (options && options.rethrow === true) {
          throw error;
        }
        return null;
      } finally {
        loadingButtons.forEach((b) => b.classList.remove('processing'));
      }
    }

    if (isUpdating) {
      if (options && options.rethrow === true) {
        throw new Error('Cart is already updating. Please try again.');
      }
      return null;
    }
    isUpdating = true;

    loadingButtons.forEach((loadingButton) => {
      loadingButton.classList.add('processing');
    });

    try {
      return await completeVerifiedAddToCart(
        [{ id: variantId, quantity: quantity }],
        {
          imageUrl,
          startFrame: clickEvent,
          openDrawer: options.openDrawer !== false,
        }
      );

    } catch (error) {
      console.error('Add to cart error:', error);
      if (!options || options.suppressErrorToast !== true) {
        showAddToCartError(error);
      }
      if (options && options.rethrow === true) {
        throw error;
      }
    } finally {
      isUpdating = false;
      loadingButtons.forEach((loadingButton) => {
        loadingButton.classList.remove('processing');
      });
    }
  }

  async function addItemsToCartAjax(cartItems, options = {}) {
    const normalizedItems = normalizeCartAddItems(cartItems);
    if (!normalizedItems.length) {
      throw new Error('Missing variant ID');
    }

    if (normalizedItems.length === 1) {
      return addToCartAjax(
        normalizedItems[0].id,
        normalizedItems[0].quantity,
        options.imageUrl || '',
        options.sourceRect || options.clickEvent || null,
        null,
        options.triggerButton || null,
        {
          openDrawer: options.openDrawer !== false,
          rethrow: true,
          suppressErrorToast: options.suppressErrorToast === true,
        }
      );
    }

    const loadingButtons = Array.from(new Set(getScopedLoadingButtons({ trigger: options.triggerButton || null })));

    if (isUpdating) {
      throw new Error('Cart is already updating. Please try again.');
    }
    isUpdating = true;
    loadingButtons.forEach((loadingButton) => {
      loadingButton.classList.add('processing');
    });

    try {
      return await completeVerifiedAddToCart(normalizedItems, {
        imageUrl: options.imageUrl || '',
        startFrame: options.sourceRect || options.clickEvent || null,
        openDrawer: options.openDrawer !== false,
      });
    } catch (error) {
      console.error('Add to cart error:', error);
      if (options.suppressErrorToast !== true) {
        showAddToCartError(error);
      }
      throw error;
    } finally {
      isUpdating = false;
      loadingButtons.forEach((loadingButton) => {
        loadingButton.classList.remove('processing');
      });
    }
  }

  // ===== FLYING ANIMATION =====
  function isElementVisible(element) {
    if (!element) return false;
    const rect = element.getBoundingClientRect();
    const style = window.getComputedStyle(element);
    return rect.width > 0 && rect.height > 0 && style.display !== 'none' && style.visibility !== 'hidden';
  }

  function getHeaderCartButton() {
    if (!Array.isArray(headerCartBtns) || !headerCartBtns.length) {
      headerCartBtns = Array.from(document.querySelectorAll(SELECTORS.headerCartBtn));
    }

    return headerCartBtns.find(isElementVisible) || headerCartBtns[0] || null;
  }

  function getStartFrame(start) {
    if (start && typeof start.left === 'number' && typeof start.top === 'number') {
      return start;
    }

    if (start && typeof start.clientX === 'number' && typeof start.clientY === 'number') {
      return {
        left: start.clientX - 30,
        top: start.clientY - 30,
        width: 60,
        height: 60,
      };
    }

    return null;
  }

  async function animateFlyToCart(imageUrl, start) {
    const cartButton = getHeaderCartButton();
    const startFrame = getStartFrame(start);
    if (!flyingItem || !flyingImage || !cartButton || !startFrame) return;

    const size = Math.max(56, Math.min(Math.max(startFrame.width || 0, startFrame.height || 0), 104));
    const startCenterX = startFrame.left + ((startFrame.width || size) / 2);
    const startCenterY = startFrame.top + ((startFrame.height || size) / 2);
    const startX = startCenterX - (size / 2);
    const startY = startCenterY - (size / 2);

    const cartRect = cartButton.getBoundingClientRect();
    const endCenterX = cartRect.left + (cartRect.width / 2);
    const endCenterY = cartRect.top + (cartRect.height / 2);
    const deltaX = endCenterX - startCenterX;
    const deltaY = endCenterY - startCenterY;

    flyingImage.src = imageUrl;
    flyingItem.style.width = `${size}px`;
    flyingItem.style.height = `${size}px`;
    flyingItem.style.left = `${startX}px`;
    flyingItem.style.top = `${startY}px`;
    flyingItem.style.opacity = '1';
    flyingItem.hidden = false;
    flyingItem.classList.add('flying');

    const animation = flyingItem.animate(
      [
        {
          transform: 'translate3d(0, 0, 0) scale(1) rotate(0deg)',
          opacity: 1,
          filter: 'blur(0px)',
        },
        {
          transform: `translate3d(${deltaX * 0.22}px, ${deltaY * 0.14 - 14}px, 0) scale(0.92) rotate(-6deg)`,
          opacity: 0.96,
          filter: 'blur(0px)',
          offset: 0.22,
        },
        {
          transform: `translate3d(${deltaX * 0.68}px, ${deltaY * 0.68 - 28}px, 0) scale(0.48) rotate(12deg)`,
          opacity: 0.82,
          filter: 'blur(0.4px)',
          offset: 0.72,
        },
        {
          transform: `translate3d(${deltaX}px, ${deltaY}px, 0) scale(0.14) rotate(22deg)`,
          opacity: 0,
          filter: 'blur(1.2px)',
        },
      ],
      {
        duration: 720,
        easing: 'cubic-bezier(0.22, 0.84, 0.18, 1)',
        fill: 'forwards',
      }
    );

    try {
      await animation.finished;
    } catch (error) {
      /* Ignore cancellation if another add-to-cart starts immediately. */
    }

    flyingItem.classList.remove('flying');
    flyingItem.hidden = true;
    flyingItem.style.opacity = '';
    flyingItem.style.width = '';
    flyingItem.style.height = '';
    flyingItem.style.left = '';
    flyingItem.style.top = '';
    bounceCartIcon();
  }

  // ===== BOUNCE CART ICON =====
  function bounceCartIcon() {
    const cartButton = getHeaderCartButton();
    if (!cartButton) return;
    cartButton.classList.add('bounce');
    setTimeout(() => {
      cartButton.classList.remove('bounce');
    }, 500);
  }

  function emitMiniCartRefreshed(cart) {
    if (typeof CustomEvent !== 'function') return;

    document.dispatchEvent(new CustomEvent('mini-cart:refreshed', {
      bubbles: true,
      detail: {
        itemCount: cart?.item_count ?? null,
      },
    }));
  }

  function isMiniCartEvent(event) {
    return event?.detail?.source === 'mini-cart';
  }

  function emitCartChanged(cart, source = 'mini-cart') {
    if (typeof CustomEvent !== 'function') return;

    const detail = {
      source,
      cart,
      itemCount: getCartItemCount(cart),
    };

    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail,
    }));
    document.dispatchEvent(new CustomEvent('cart:updated', {
      bubbles: true,
      detail,
    }));
  }

  function normalizeAddedCartItem(payload) {
    if (!payload) return null;
    if (Array.isArray(payload.items)) return payload.items[0] || null;
    return payload;
  }

  function normalizeCartAddItems(cartItems) {
    const inputItems = Array.isArray(cartItems) ? cartItems : [cartItems];

    return inputItems.map((item) => {
      if (!item) return null;
      const normalized = { ...item };
      normalized.id = normalized.id || normalized.variantId || normalized.variant_id;
      delete normalized.variantId;
      delete normalized.variant_id;
      normalized.quantity = Math.max(1, Number.parseInt(String(normalized.quantity || 1), 10) || 1);
      return normalized.id ? normalized : null;
    }).filter(Boolean);
  }

  function getAddedItemList(payload) {
    if (!payload) return [];
    if (Array.isArray(payload.items)) return payload.items;
    return [payload];
  }

  function findAddedCartItem(cart, payload, cartItems) {
    const addedItems = getAddedItemList(payload);

    for (const addedItem of addedItems) {
      const key = addedItem?.key ? String(addedItem.key) : '';
      if (!key) continue;
      const matchedItem = getCartItemByKey(cart, key);
      if (matchedItem) return matchedItem;
    }

    const firstVariantId = cartItems[0]?.id;
    if (!firstVariantId || !isCartPayload(cart)) return normalizeAddedCartItem(payload);
    return cart.items.find((item) => String(item?.variant_id || '') === String(firstVariantId)) || normalizeAddedCartItem(payload);
  }

  function normalizeNewlyAddedItem(newlyAddedItem = null) {
    if (!newlyAddedItem) return null;

    if (typeof newlyAddedItem === 'object') {
      return {
        key: newlyAddedItem.key ? String(newlyAddedItem.key) : null,
        variantId: newlyAddedItem.variantId || newlyAddedItem.variant_id || null,
        scrollToTop: newlyAddedItem.scrollToTop === true,
      };
    }

    return {
      key: null,
      variantId: newlyAddedItem,
      scrollToTop: false,
    };
  }

  function getNewlyAddedIndex(cartItems, newlyAddedItem) {
    if (!Array.isArray(cartItems) || !newlyAddedItem) return -1;

    if (newlyAddedItem.key) {
      const keyIndex = cartItems.findIndex((item) => String(item.key) === newlyAddedItem.key);
      if (keyIndex !== -1) return keyIndex;
    }

    if (newlyAddedItem.variantId) {
      return cartItems.findIndex((item) => item.variant_id == newlyAddedItem.variantId);
    }

    return -1;
  }

  function getDisplayCartItems(cart) {
    if (!isCartPayload(cart)) return [];

    return cart.items.filter((item) => Number(item?.quantity) > 0);
  }

  function isCartPayload(payload) {
    return Boolean(payload && typeof payload === 'object' && Array.isArray(payload.items));
  }

  function delay(ms) {
    return new Promise((resolve) => {
      window.setTimeout(resolve, ms);
    });
  }

  function getCartItemCount(cart) {
    const count = Number(cart?.item_count);
    return Number.isFinite(count) ? Math.max(0, Math.floor(count)) : 0;
  }

  function getCartTotalPrice(cart) {
    const total = Number(cart?.total_price);
    return Number.isFinite(total) ? total : 0;
  }

  function routePath(path) {
    let root = '/';

    try {
      root = window.Shopify?.routes?.root || window.Theme?.routes?.root_url || '/';
    } catch (error) {
      root = '/';
    }

    return String(root || '/').replace(/\/?$/, '/') + String(path || '').replace(/^\//, '');
  }

  function mergeCartHeaders(headers) {
    const output = { 'Accept': 'application/json' };

    if (headers instanceof Headers) {
      headers.forEach((value, key) => {
        output[key] = value;
      });
      return output;
    }

    Object.keys(headers || {}).forEach((key) => {
      output[key] = headers[key];
    });

    return output;
  }

  async function readJsonResponse(response) {
    let text = '';

    try {
      text = await response.text();
    } catch (error) {
      return null;
    }

    if (!text) return null;

    try {
      return JSON.parse(text);
    } catch (error) {
      return {
        invalidJson: true,
        responseText: text.slice(0, 500),
      };
    }
  }

  async function fetchCartJson(path, options = {}, fallback = 'Unable to update cart') {
    const init = {
      ...options,
      credentials: options.credentials || 'same-origin',
      headers: mergeCartHeaders(options.headers),
    };
    const response = await fetch(routePath(path), init);
    const payload = await readJsonResponse(response);

    if (!response.ok) {
      throw createCartApiError(response, payload, fallback);
    }

    return payload;
  }

  function getCartApiErrorMessage(payload, fallback = 'Unable to update cart') {
    const candidate =
      payload?.description ||
      payload?.message ||
      payload?.error ||
      (payload instanceof Error ? payload.message : '');

    return typeof candidate === 'string' && candidate.trim()
      ? candidate.trim()
      : fallback;
  }

  function parseLimitQuantity(message, fallbackQuantity = null) {
    const text = String(message || '');
    const patterns = [
      /\bonly\s+(\d+)\s+items?\b/i,
      /\blimited\s+to\s+(\d+)\b/i,
      /\bmaximum\s+quantity(?:\s+of\s+this\s+item)?(?:\s+is)?\s+(\d+)\b/i,
      /\bmax(?:imum)?\s+(\d+)\b/i,
    ];

    for (const pattern of patterns) {
      const match = text.match(pattern);
      const parsed = Number.parseInt(match?.[1] || '', 10);
      if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }

    const fallback = Number.parseInt(String(fallbackQuantity || ''), 10);
    return Number.isFinite(fallback) && fallback > 0 ? fallback : null;
  }

  function formatCartLimitBadgeMessage(message, fallbackQuantity = null) {
    const text = String(message || '');
    const normalized = text.toLowerCase();
    const quantity = parseLimitQuantity(text, fallbackQuantity);
    const quantityText = quantity ? String(quantity) : '';

    if (
      normalized.includes('availability') ||
      normalized.includes('available quantity') ||
      normalized.includes('inventory') ||
      normalized.includes('stock')
    ) {
      return quantityText
        ? `(Limited to ${quantityText} due to low inventory)`
        : '(Limited due to low inventory)';
    }

    if (
      normalized.includes('per customer') ||
      normalized.includes('maximum quantity') ||
      normalized.includes('already in your cart') ||
      normalized.includes('cannot add more')
    ) {
      return quantityText
        ? `(Only ${quantityText} per customer)`
        : '(Customer quantity limit)';
    }

    return quantityText
      ? `(Limited to ${quantityText})`
      : '(Limited quantity)';
  }

  function getCartItemByKey(cart, itemKey) {
    if (!isCartPayload(cart) || !itemKey) return null;
    return cart.items.find((item) => String(item?.key || '') === String(itemKey)) || null;
  }

  function setCartLimitMessage(itemKey, message, quantity = null) {
    if (!itemKey) return;
    _cartLimitMessages.set(String(itemKey), formatCartLimitBadgeMessage(message, quantity));
  }

  function clearCartLimitMessage(itemKey) {
    if (!itemKey) return;
    _cartLimitMessages.delete(String(itemKey));
  }

  function getCartLimitBadgeHtml(itemKey) {
    const message = _cartLimitMessages.get(String(itemKey || ''));
    if (!message) {
      return '<span class="mini-cart-item__limit-badge" data-cart-limit-message hidden></span>';
    }

    return `<span class="mini-cart-item__limit-badge" data-cart-limit-message>${escapeHtml(message)}</span>`;
  }

  function syncCartLimitBadges(cart) {
    if (!items) return;

    if (isCartPayload(cart)) {
      const currentKeys = new Set(cart.items.map((item) => String(item?.key || '')).filter(Boolean));
      Array.from(_cartLimitMessages.keys()).forEach((key) => {
        if (!currentKeys.has(key)) _cartLimitMessages.delete(key);
      });
    }

    items.querySelectorAll(SELECTORS.item).forEach((itemEl) => {
      const key = itemEl.dataset.lineItemKey;
      const message = key ? _cartLimitMessages.get(String(key)) : '';
      let badge = itemEl.querySelector('[data-cart-limit-message]');
      const qtyRow = itemEl.querySelector('.mini-cart-item__qty-row');

      if (!badge && qtyRow) {
        badge = document.createElement('span');
        badge.className = 'mini-cart-item__limit-badge';
        badge.setAttribute('data-cart-limit-message', '');
        qtyRow.appendChild(badge);
      }

      if (!(badge instanceof HTMLElement)) return;

      if (message) {
        badge.textContent = message;
        badge.hidden = false;
      } else {
        badge.textContent = '';
        badge.hidden = true;
      }
    });
  }

  function createCartApiError(response, payload, fallback) {
    const error = new Error(getCartApiErrorMessage(payload, fallback));
    error.status = response?.status || 0;
    error.url = response?.url || '';
    error.payload = payload;
    return error;
  }

  async function fetchCartSnapshot() {
    const cart = await fetchCartJson('cart.js', {
      method: 'GET',
      cache: 'no-store',
    }, 'Unable to refresh cart');

    if (!isCartPayload(cart)) {
      throw createCartApiError(null, cart, 'Unable to refresh cart');
    }

    return cart;
  }

  async function requestCartAddItems(cartItems) {
    const recommendations = window.MrMapleRecommendations;
    // Attribution is optional and must never prevent or replay a cart write.
    try {
      if (recommendations?.attributeCartItems) cartItems = recommendations.attributeCartItems(cartItems);
    } catch (_) {}
    const payload = await fetchCartJson('cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        items: cartItems
      })
    }, 'Failed to add to cart');
    try { recommendations?.recordCartAdd?.(payload); } catch (_) {}
    return payload;
  }

  async function readCartForDisplay() {
    // GET /cart.js is idempotent, so a transient read failure can be retried
    // without any risk of duplicating a write.
    try {
      return await fetchCartSnapshot();
    } catch (error) {
      await delay(CART_VERIFY_DELAY_MS);
      return await fetchCartSnapshot();
    }
  }

  async function addCartItemsWithVerification(cartItems) {
    // POST /cart/add.js exactly once. It is authoritative: a 2xx response means
    // the lines are committed, and a non-2xx throws with Shopify's real message
    // (e.g. inventory limits). The write is never replayed, so a duplicate add
    // is impossible. The add response only carries the added lines, so the full
    // cart is read separately for rendering totals.
    const payload = await requestCartAddItems(cartItems);
    try {
      const cart = await readCartForDisplay();

      return {
        payload,
        cart,
        addedItem: findAddedCartItem(cart, payload, cartItems),
      };
    } catch (error) {
      console.warn('Unable to refresh cart after add:', error);

      return {
        payload,
        cart: null,
        addedItem: normalizeAddedCartItem(payload),
        refreshError: error,
      };
    }
  }

  async function requestCartChange(itemKey, quantity) {
    const targetQuantity = Math.max(0, Number.parseInt(String(quantity || 0), 10) || 0);

    // POST /cart/change.js exactly once. It returns the full, authoritative
    // cart on success and throws Shopify's real error otherwise. The write is
    // never replayed, so a quantity change or removal can never be duplicated.
    const cart = await fetchCartJson('cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        id: itemKey,
        quantity: targetQuantity
      })
    }, 'Unable to update cart');

    if (!isCartPayload(cart)) {
      throw createCartApiError(null, cart, 'Unable to update cart');
    }

    return cart;
  }

  function syncCartSnapshot(cart, options = {}) {
    if (!isCartPayload(cart)) return false;

    const {
      newlyAddedItem = null,
      renderOptions = {},
      preferInPlace = true,
    } = options;

    if (!preferInPlace || !updateCartItemsInPlace(cart)) {
      renderCartItems(cart, newlyAddedItem, renderOptions);
    }
    updateCartUI(cart);
    updateHeaderCartCount(cart);
    syncCartLimitBadges(cart);
    emitMiniCartRefreshed(cart);
    return true;
  }

  async function recoverFromCartChangeError(error, fallbackMessage = 'Unable to update cart', context = {}) {
    const message = getCartApiErrorMessage(error?.payload || error, error?.message || fallbackMessage);
    showToast(message, 'error', getCartLimitToastOptions(message));

    try {
      const cart = await fetchCartSnapshot();
      const affectedItem = getCartItemByKey(cart, context.itemKey);
      if (context.itemKey) {
        setCartLimitMessage(
          context.itemKey,
          message,
          affectedItem?.quantity ?? context.fallbackQuantity ?? context.attemptedQuantity ?? null
        );
      }
      syncCartSnapshot(cart, { preferInPlace: false });
    } catch (refreshError) {
      console.error('Error recovering cart after quantity change:', refreshError);
      await refreshCart();
    }
  }

  function getFiniteNumber(...values) {
    for (const value of values) {
      const number = Number(value);
      if (Number.isFinite(number)) return number;
    }

    return null;
  }

  function getCartLinePrice(item) {
    const directPrice = getFiniteNumber(item?.final_line_price, item?.line_price);
    if (directPrice !== null) return directPrice;

    const quantity = Math.max(1, Number(item?.quantity) || 1);
    const unitPrice = getFiniteNumber(item?.final_price, item?.discounted_price, item?.price, item?.original_price);
    return unitPrice !== null ? unitPrice * quantity : 0;
  }

  function getCartOriginalLinePrice(item) {
    const directPrice = getFiniteNumber(item?.original_line_price);
    if (directPrice !== null) return directPrice;

    const quantity = Math.max(1, Number(item?.quantity) || 1);
    const unitPrice = getFiniteNumber(item?.original_price, item?.compare_at_price);
    return unitPrice !== null ? unitPrice * quantity : getCartLinePrice(item);
  }

  function getCartLinePriceHtml(item) {
    const linePrice = getCartLinePrice(item);
    const originalLinePrice = getCartOriginalLinePrice(item);
    let priceHtml = formatMoney(linePrice);

    if (originalLinePrice > linePrice) {
      priceHtml += ` <span class="mini-cart-item__price-compare">${formatMoney(originalLinePrice)}</span>`;
    }

    return priceHtml;
  }

  // ===== REFRESH CART =====
  async function refreshCart(newlyAddedItem = null) {
    try {
      drawer?.classList.add('loading');

      const cart = await fetchCartSnapshot();
      syncCartSnapshot(cart, {
        newlyAddedItem,
        preferInPlace: false,
      });
      return true;

    } catch (error) {
      console.error('Error fetching cart:', error);
      return false;
    } finally {
      drawer?.classList.remove('loading');
    }
  }

  // ===== RENDER CART ITEMS =====
  function renderCartItems(cart, newlyAddedItem = null, renderOptions = {}) {
    if (!items || !isCartPayload(cart)) return;

    const cartItems = getDisplayCartItems(cart);
    const sourceCartItems = Array.isArray(cart.items) ? cart.items : [];

    if (cartItems.length === 0) {
      items.innerHTML = '';
      empty?.classList.remove('hidden');
      items.classList.add('hidden');
      footer?.classList.add('hidden');
      return;
    }

    empty?.classList.add('hidden');
    items.classList.remove('hidden');
    footer?.classList.remove('hidden');

    const addedItem = normalizeNewlyAddedItem(newlyAddedItem);
    const newlyAddedIndex = getNewlyAddedIndex(cartItems, addedItem);
    const displayItems = newlyAddedIndex > 0
      ? [
        cartItems[newlyAddedIndex],
        ...cartItems.slice(0, newlyAddedIndex),
        ...cartItems.slice(newlyAddedIndex + 1),
      ]
      : cartItems;

    const suppressItemAnimation = renderOptions.suppressItemAnimation === true;
    const itemsHtml = displayItems.map((item, index) => {
      const itemKey = item?.key ? String(item.key) : '';
      const itemUrl = item?.url || '#';
      const itemTitle = item?.product_title || item?.title || 'Cart item';
      const itemImage = typeof item?.image === 'string' ? item.image : '';
      const itemVariantId = item?.variant_id || '';
      const itemQuantity = Math.max(0, Number(item?.quantity) || 0);
      const isNewlyAdded = newlyAddedIndex !== -1 && (
        (addedItem?.key && itemKey === addedItem.key) ||
        (!addedItem?.key && itemVariantId == addedItem?.variantId)
      );
      const itemClasses = [
        'mini-cart-item',
        isNewlyAdded ? 'just-added' : '',
        suppressItemAnimation && !isNewlyAdded ? 'no-enter' : '',
      ].filter(Boolean).join(' ');
      const animationDelay = suppressItemAnimation ? '' : `animation-delay: ${index * 0.05}s`;
      const matchingIndex = sourceCartItems.findIndex((cartItem) => String(cartItem?.key || '') === itemKey);
      const lineIndex = matchingIndex >= 0 ? matchingIndex + 1 : index + 1;
      return `
        <div 
          class="${itemClasses}" 
          data-mini-cart-item
          data-line-item-key="${escapeHtml(itemKey)}"
          data-product-id="${escapeHtml(item?.product_id || '')}"
          data-product-handle="${escapeHtml(item.handle || '')}"
          data-variant-id="${escapeHtml(itemVariantId)}"
          data-line-index="${lineIndex}"
          style="${animationDelay}"
        >
          <a href="${escapeHtml(itemUrl)}" class="mini-cart-item__image-link">
            ${itemImage ? `
              <img 
                src="${escapeHtml(getSizedImageUrl(itemImage, '200x'))}"
                alt="${escapeHtml(item.title || itemTitle)}"
                class="mini-cart-item__image"
                loading="lazy"
                width="80"
                height="80"
              >
            ` : `
              <div class="mini-cart-item__image-placeholder">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" stroke-width="1.5"/>
                  <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/>
                  <path d="M21 15l-5-5L5 21" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
            `}
          </a>
          
          <div class="mini-cart-item__details">
            <a href="${escapeHtml(itemUrl)}" class="mini-cart-item__title">
              ${escapeHtml(itemTitle)}
            </a>
            ${item.variant_title && item.variant_title !== 'Default Title' ? `
              <p class="mini-cart-item__variant">${escapeHtml(item.variant_title)}</p>
            ` : ''}
            <p class="mini-cart-item__price">
              ${getCartLinePriceHtml(item)}
            </p>
            
            <div class="mini-cart-item__actions">
              <div class="mini-cart-item__qty-row">
                <div class="mini-cart-item__quantity">
                  <button
                    type="button"
                    class="mini-cart-item__qty-btn"
                    data-qty-change="-1"
                    aria-label="Decrease quantity"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path d="M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                  </button>
                  <span class="mini-cart-item__qty-value" data-item-qty>${itemQuantity}</span>
                  <button
                    type="button"
                    class="mini-cart-item__qty-btn"
                    data-qty-change="1"
                    aria-label="Increase quantity"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                  </button>
                </div>
                ${getCartLimitBadgeHtml(itemKey)}
              </div>
              
              <button 
                type="button" 
                class="mini-cart-item__remove" 
                data-remove-item
                aria-label="Remove item"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      `;
    }).join('');

    items.innerHTML = itemsHtml;

    if (addedItem?.scrollToTop && newlyAddedIndex !== -1 && content) {
      requestAnimationFrame(() => {
        content.scrollTo({ top: 0, behavior: 'smooth' });
      });
    }
  }

  // ===== UPDATE CART UI =====
  function updateCartUI(cart) {
    if (!isCartPayload(cart)) return;

    if (countEl) {
      countEl.textContent = `(${getCartItemCount(cart)})`;
    }
    if (subtotalEl) {
      subtotalEl.textContent = formatMoney(getCartTotalPrice(cart));
    }
  }

  // ===== UPDATE HEADER CART COUNT =====
  function updateHeaderCartCount(cart = null) {
    const countElements = document.querySelectorAll(SELECTORS.headerCartCount);
    if (!countElements.length) return;

    if (isCartPayload(cart)) {
      const itemCount = getCartItemCount(cart);
      const count = itemCount > 0 ? String(itemCount) : '';
      countElements.forEach((countEl) => {
        countEl.textContent = count;
        countEl.dataset.headerCartCount = count;
        countEl.classList.toggle('visible', itemCount > 0);
      });
    } else if (cart) {
      return;
    } else {
      // Fetch cart to get count
      fetchCartSnapshot()
        .then(cart => {
          const itemCount = getCartItemCount(cart);
          const count = itemCount > 0 ? String(itemCount) : '';
          countElements.forEach((countEl) => {
            countEl.textContent = count;
            countEl.dataset.headerCartCount = count;
            countEl.classList.toggle('visible', itemCount > 0);
          });
        })
        .catch((error) => {
          console.error('Error updating cart count:', error);
        });
    }
  }

  // ===== QUANTITY CHANGE HANDLER =====
  const _qtyMutators = new Map();

  function getQtyMutator(itemKey, itemEl, qtyEl) {
    if (_qtyMutators.has(itemKey)) return _qtyMutators.get(itemKey);

    const factory = window.MrMapleOptimistic?.createDebouncedMutation;
    if (!factory) return null;

    const mutator = factory({
      render(qty) {
        if (qtyEl && document.body.contains(qtyEl)) {
          qtyEl.textContent = qty;
        }
        if (itemEl && document.body.contains(itemEl)) {
          itemEl.classList.add('is-syncing');
          itemEl.style.opacity = '0.6';
        }
      },
      async commit(qty) {
        const cart = await requestCartChange(itemKey, qty);
        clearCartLimitMessage(itemKey);
        syncCartSnapshot(cart);

        if (itemEl && document.body.contains(itemEl)) {
          itemEl.classList.remove('is-syncing');
          itemEl.style.opacity = '1';
        }
      },
      onError(error, lastCommitted) {
        void recoverFromCartChangeError(error, 'Unable to update cart', {
          itemKey,
          attemptedQuantity: Number.parseInt(qtyEl?.textContent || '', 10) || null,
          fallbackQuantity: lastCommitted,
        });
        if (itemEl && document.body.contains(itemEl)) {
          itemEl.classList.remove('is-syncing');
          itemEl.style.opacity = '1';
        }
      },
      debounceMs: 400,
    });

    if (typeof mutator.setCommitted === 'function') {
      const initialQty = Number.parseInt(qtyEl?.textContent || '0', 10);
      mutator.setCommitted(Number.isFinite(initialQty) ? Math.max(0, initialQty) : 0);
    }

    _qtyMutators.set(itemKey, mutator);
    return mutator;
  }

  async function handleQuantityChange(btn) {
    const change = parseInt(btn.dataset.qtyChange);
    const itemEl = btn.closest(SELECTORS.item);
    if (!itemEl) return;

    const itemKey = itemEl.dataset.lineItemKey;
    const qtyEl = itemEl.querySelector(SELECTORS.itemQty);
    if (!itemKey || !qtyEl) return;

    const currentQty = Number.parseInt(qtyEl.textContent || '0', 10) || 0;
    const newQty = Math.max(0, currentQty + change);

    if (newQty === 0) {
      handleRemoveItem(itemEl.querySelector(SELECTORS.removeItem));
      return;
    }

    const mutator = getQtyMutator(itemKey, itemEl, qtyEl);
    if (mutator) {
      mutator.setValue(newQty);
      return;
    }

    qtyEl.textContent = newQty;
    itemEl.classList.add('is-syncing');
    itemEl.style.opacity = '0.6';

    window._cartUpdateTimers = window._cartUpdateTimers || {};
    if (window._cartUpdateTimers[itemKey]) {
      clearTimeout(window._cartUpdateTimers[itemKey]);
    }

    window._cartUpdateTimers[itemKey] = setTimeout(async () => {
      try {
        const cart = await requestCartChange(itemKey, newQty);
        clearCartLimitMessage(itemKey);
        syncCartSnapshot(cart);

      } catch (error) {
        console.error('Error updating quantity:', error);
        await recoverFromCartChangeError(error, 'Unable to update cart', {
          itemKey,
          attemptedQuantity: newQty,
          fallbackQuantity: currentQty,
        });
      } finally {
        if (itemEl && document.body.contains(itemEl)) {
          itemEl.classList.remove('is-syncing');
          itemEl.style.opacity = '1';
        }
      }
    }, 400);
  }

  // Helper to update specific items without re-creating DOM to prevent animation flashes
  function updateCartItemsInPlace(cart) {
    if (!isCartPayload(cart)) return false;

    const cartItems = getDisplayCartItems(cart);
    if (!items || items.querySelectorAll(SELECTORS.item).length !== cartItems.length) {
      return false; // Items added or removed, fallback to full render
    }

    const itemEls = items.querySelectorAll(SELECTORS.item);
    const itemElsByKey = new Map(Array.from(itemEls).map((el) => [el.dataset.lineItemKey, el]));
    let allMatched = true;

    cartItems.forEach((item) => {
      const el = itemElsByKey.get(item.key);
      if (!el) {
        allMatched = false;
        return;
      }

      el.dataset.lineIndex = String(cart.items.findIndex((cartItem) => cartItem.key === item.key) + 1);

      const qtyEl = el.querySelector(SELECTORS.itemQty);
      if (qtyEl && qtyEl.textContent != item.quantity) {
        qtyEl.textContent = item.quantity;
      }

      const mutator = _qtyMutators.get(item.key);
      if (mutator && typeof mutator.setCommitted === 'function') {
        const committedQty = Number.parseInt(String(item.quantity), 10);
        mutator.setCommitted(Number.isFinite(committedQty) ? Math.max(0, committedQty) : 0);
      }

      const priceEl = el.querySelector('.mini-cart-item__price');
      if (priceEl) {
        const priceHtml = getCartLinePriceHtml(item);
        const currentPrice = priceEl.innerHTML.replace(/\s+/g, ' ').trim();
        const newPrice = priceHtml.replace(/\s+/g, ' ').trim();
        if (currentPrice !== newPrice) {
          priceEl.innerHTML = priceHtml;
        }
      }
    });

    return allMatched;
  }

  function getTimeListMs(value) {
    return String(value || '0s')
      .split(',')
      .map((part) => {
        const trimmed = part.trim();
        const parsed = parseFloat(trimmed);
        if (!Number.isFinite(parsed)) return 0;
        return trimmed.endsWith('ms') ? parsed : parsed * 1000;
      });
  }

  function getMaxTransitionMs(element) {
    const style = window.getComputedStyle(element);
    const durations = getTimeListMs(style.transitionDuration);
    const delays = getTimeListMs(style.transitionDelay);
    const count = Math.max(durations.length, delays.length, 1);

    let maxMs = 0;
    for (let index = 0; index < count; index += 1) {
      const duration = durations[index] ?? durations[durations.length - 1] ?? 0;
      const delay = delays[index] ?? delays[delays.length - 1] ?? 0;
      maxMs = Math.max(maxMs, duration + delay);
    }

    return maxMs;
  }

  function collapseCartItem(itemEl) {
    if (!itemEl) return Promise.resolve();

    const itemHeight = itemEl.getBoundingClientRect().height;
    const previousTransition = itemEl.style.transition;
    itemEl.style.transition = 'none';
    itemEl.style.setProperty('--mini-cart-item-height', `${Math.ceil(itemHeight)}px`);
    itemEl.classList.add('is-collapsing', 'no-enter');
    void itemEl.offsetHeight;
    itemEl.style.transition = previousTransition;
    void itemEl.offsetHeight;

    return new Promise((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        itemEl.removeEventListener('transitionend', handleTransitionEnd);
        resolve();
      };
      const handleTransitionEnd = (event) => {
        if (event.target !== itemEl) return;
        if (event.propertyName !== 'max-height') return;
        finish();
      };

      itemEl.addEventListener('transitionend', handleTransitionEnd);
      itemEl.classList.add('removing');
      const transitionMs = getMaxTransitionMs(itemEl);
      window.setTimeout(finish, Math.max(transitionMs + 80, 80));
    });
  }

  function restoreCollapsedCartItem(itemEl) {
    if (!itemEl) return;
    itemEl.classList.remove('is-collapsing', 'removing', 'no-enter');
    itemEl.style.removeProperty('--mini-cart-item-height');
  }

  function applyCartAfterItemRemoval(cart, removedItemEl) {
    if (!isCartPayload(cart)) return;

    if (!getDisplayCartItems(cart).length) {
      removedItemEl?.remove();
      items.innerHTML = '';
      empty?.classList.remove('hidden');
      items.classList.add('hidden');
      footer?.classList.add('hidden');
      return;
    }

    empty?.classList.add('hidden');
    items.classList.remove('hidden');
    footer?.classList.remove('hidden');

    removedItemEl?.remove();

    if (!updateCartItemsInPlace(cart)) {
      renderCartItems(cart, null, { suppressItemAnimation: true });
    }
  }

  // ===== REMOVE ITEM HANDLER =====
  async function handleRemoveItem(btn) {
    if (!btn || btn.classList.contains('removing')) return;

    const itemEl = btn.closest(SELECTORS.item);
    if (!itemEl) return;

    const itemKey = itemEl.dataset.lineItemKey;

    btn.classList.add('removing');
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');
    const collapsePromise = collapseCartItem(itemEl);

    try {
      // Clear any pending debounced updates for this item
      if (window._cartUpdateTimers && window._cartUpdateTimers[itemKey]) {
        clearTimeout(window._cartUpdateTimers[itemKey]);
        delete window._cartUpdateTimers[itemKey];
      }
      _qtyMutators.delete(itemKey);
      clearCartLimitMessage(itemKey);

      const cart = await requestCartChange(itemKey, 0);

      await collapsePromise;
      applyCartAfterItemRemoval(cart, itemEl);
      updateCartUI(cart);
      updateHeaderCartCount(cart);
      syncCartLimitBadges(cart);
      emitMiniCartRefreshed(cart);

    } catch (error) {
      console.error('Error removing item:', error);
      showToast(
        getCartApiErrorMessage(error?.payload || error, error?.message || 'Unable to remove item'),
        'error'
      );
      btn.classList.remove('removing');
      btn.disabled = false;
      btn.removeAttribute('aria-busy');
      itemEl.classList.remove('removing');
      restoreCollapsedCartItem(itemEl);
    }
  }

  // ===== OPEN DRAWER =====
  function openDrawer() {
    if (!drawer || isOpen) return;

    isOpen = true;
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('mini-cart-open');

    // Focus trap - focus first focusable element without scrolling
    requestAnimationFrame(() => {
      const firstFocusable = drawer.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      focusElementWithoutScroll(firstFocusable);
    });
  }

  // ===== CLOSE DRAWER =====
  function closeDrawer() {
    if (!drawer || !isOpen) return;

    isOpen = false;
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('mini-cart-open');

    // Return focus to cart button
    focusElementWithoutScroll(getHeaderCartButton());
  }

  // ===== UTILITIES =====
  function formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    // Use Shopify's money format or default
    const format = window.theme?.moneyFormat || '${{amount}}';
    return format.replace('{{amount}}', amount).replace('{{amount_no_decimals}}', Math.round(cents / 100));
  }

  function getSizedImageUrl(src, size) {
    if (!src) return '';
    // Shopify image URL sizing
    if (src.includes('cdn.shopify.com')) {
      return src.replace(/(_\d+x\d+)?(\.[a-z]+)(\?.*)?$/i, `_${size}$2$3`);
    }
    return src;
  }

  function escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = String(str ?? '');
    return div.innerHTML;
  }

  function getCartLimitToastOptions(message) {
    const normalizedMessage = String(message || '').toLowerCase();
    const shouldOfferCartAction =
      normalizedMessage.includes('maximum quantity') ||
      normalizedMessage.includes('already in your cart') ||
      normalizedMessage.includes('available quantity') ||
      normalizedMessage.includes('cannot add more');

    if (!shouldOfferCartAction) return {};

    return {
      actionLabel: 'Open cart',
      actionAriaLabel: 'Open cart drawer',
      onAction: () => {
        if (drawer) {
          openDrawer();
          return;
        }

        window.location.href = '/cart';
      },
    };
  }

  function showAddToCartError(error) {
    const message = error?.message || 'Unable to add item to cart';
    showToast(message, 'error', getCartLimitToastOptions(message));
  }

  function showToast(message, type = 'info', options = {}) {
    const config =
      type && typeof type === 'object'
        ? { ...type }
        : { ...options, type };
    const variant =
      typeof config.type === 'string' && config.type.trim()
        ? config.type.trim()
        : 'info';
    const duration = Number.isFinite(config.duration)
      ? Number(config.duration)
      : config.actionLabel
        ? 8000
        : 3000;

    // Use existing toast system if available
    if (window.showToast) {
      window.showToast(message, { ...config, type: variant });
      return;
    }

    const sharedToast = document.querySelector('.wl-toast');
    if (sharedToast instanceof HTMLElement) {
      const messageEl = sharedToast.querySelector('.wl-toast-message');
      if (messageEl instanceof HTMLElement) {
        messageEl.textContent = message;
      }

      let actionsEl = sharedToast.querySelector('.wl-toast-actions');
      if (!(actionsEl instanceof HTMLElement)) {
        actionsEl = document.createElement('div');
        actionsEl.className = 'wl-toast-actions';
        sharedToast.appendChild(actionsEl);
      }
      actionsEl.replaceChildren();

      sharedToast.classList.remove('wl-toast--success', 'wl-toast--error', 'wl-toast--info', 'wl-toast--busy');
      sharedToast.classList.add(`wl-toast--${variant}`);
      sharedToast.removeAttribute('hidden');
      sharedToast.setAttribute('aria-hidden', 'false');
      void sharedToast.offsetHeight;

      if (toastTimer) {
        clearTimeout(toastTimer);
      }

      const dismissToast = () => {
        sharedToast.setAttribute('aria-hidden', 'true');
        sharedToast.setAttribute('hidden', 'true');
        sharedToast.classList.remove('wl-toast--busy');
        actionsEl.replaceChildren();
        if (toastTimer) {
          clearTimeout(toastTimer);
          toastTimer = null;
        }
      };

      const closeBtn = sharedToast.querySelector('.wl-toast-close');
      if (closeBtn instanceof HTMLElement) {
        closeBtn.setAttribute('tabindex', '0');
        closeBtn.setAttribute('aria-hidden', 'false');
        closeBtn.onclick = dismissToast;
      }

      if (config.actionLabel && typeof config.onAction === 'function') {
        const actionButton = document.createElement('button');
        actionButton.type = 'button';
        actionButton.className = 'wl-toast-action';
        actionButton.textContent = String(config.actionLabel);
        if (config.actionAriaLabel) {
          actionButton.setAttribute('aria-label', String(config.actionAriaLabel));
        }
        actionButton.addEventListener('click', async () => {
          if (actionButton.disabled) return;
          actionButton.disabled = true;
          sharedToast.classList.add('wl-toast--busy');
          try {
            await config.onAction();
            if (config.closeOnAction !== false) {
              dismissToast();
            }
          } catch (toastError) {
            console.error('Toast action failed:', toastError);
            actionButton.disabled = false;
            sharedToast.classList.remove('wl-toast--busy');
          }
        });
        actionsEl.appendChild(actionButton);
      }

      if (duration > 0) {
        toastTimer = setTimeout(dismissToast, duration);
      }
      return;
    }

    // Simple fallback
    const toast = document.createElement('div');
    toast.className = `wl-toast wl-toast--${variant}`;

    const messageEl = document.createElement('span');
    messageEl.className = 'wl-toast-message';
    messageEl.textContent = message;
    toast.appendChild(messageEl);

    if (config.actionLabel && typeof config.onAction === 'function') {
      const actionsEl = document.createElement('div');
      actionsEl.className = 'wl-toast-actions';

      const actionButton = document.createElement('button');
      actionButton.type = 'button';
      actionButton.className = 'wl-toast-action';
      actionButton.textContent = String(config.actionLabel);
      if (config.actionAriaLabel) {
        actionButton.setAttribute('aria-label', String(config.actionAriaLabel));
      }
      actionButton.addEventListener('click', async () => {
        if (actionButton.disabled) return;
        actionButton.disabled = true;
        try {
          await config.onAction();
          if (config.closeOnAction !== false) {
            toast.remove();
          }
        } catch (toastError) {
          console.error('Toast action failed:', toastError);
          actionButton.disabled = false;
        }
      });

      actionsEl.appendChild(actionButton);
      toast.appendChild(actionsEl);
    }

    document.body.appendChild(toast);

    if (duration > 0) {
      setTimeout(() => toast.remove(), duration);
    }
  }

  function focusElementWithoutScroll(element) {
    if (!element || typeof element.focus !== 'function') return;

    try {
      element.focus({ preventScroll: true });
    } catch (error) {
      element.focus();
    }
  }

  // ===== INITIALIZE ON DOM READY =====
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for external use
  window.MiniCart = {
    open: openDrawer,
    close: closeDrawer,
    refresh: refreshCart,
    addItems: async function addItems(itemsOrPayload, maybeOptions = {}) {
      const payload = Array.isArray(itemsOrPayload)
        ? { ...maybeOptions, items: itemsOrPayload }
        : { ...maybeOptions, ...(itemsOrPayload || {}) };
      const cartItems = payload.items || (payload.id || payload.variantId ? [payload] : []);

      return addItemsToCartAjax(cartItems, {
        imageUrl: payload.imageUrl || '',
        sourceRect: payload.sourceRect || null,
        clickEvent: payload.clickEvent || null,
        triggerButton: payload.triggerButton || null,
        openDrawer: payload.openDrawer !== false,
        suppressErrorToast: payload.suppressErrorToast === true,
      });
    },
    addItem: async function addItem(optionsOrVariantId, maybeOptions = {}) {
      const payload = optionsOrVariantId && typeof optionsOrVariantId === 'object'
        ? optionsOrVariantId
        : { ...maybeOptions, variantId: optionsOrVariantId };

      const variantId = payload?.variantId;
      if (!variantId) {
        throw new Error('Missing variant ID');
      }

      await addToCartAjax(
        String(variantId),
        Number(payload.quantity) || 1,
        payload.imageUrl || '',
        payload.sourceRect || payload.clickEvent || null,
        null,
        payload.triggerButton || null,
        {
          openDrawer: payload.openDrawer !== false,
          rethrow: true,
          suppressErrorToast: payload.suppressErrorToast === true,
        }
      );

      return true;
    }
  };

})();
